import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signIn } from '../../auth/client.ts';
import { TeacherShell } from './Layout.tsx';

/**
 * Teacher sign-in (D5).
 *
 * Google for Education OAuth is the eventual primary path, but the env
 * creds aren't wired yet — we render the button as disabled with a
 * "Coming soon" badge so teachers see the planned shape and don't
 * mistake email/password for the only future option.
 *
 * Email/password is wired against better-auth's signIn.email() which
 * issues the same session cookie the existing parent flow uses.
 *
 * After successful sign-in the page navigates to /teacher/dashboard,
 * which then calls /api/school/teacher/upsert to materialise the
 * school.teacher row that marks this user as a teacher.
 */
export function TeacherSignIn(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await signIn.email({ email, password });
      if (res.error) {
        setError(res.error.message ?? 'Sign-in failed. Check your email and password.');
        return;
      }
      navigate('/teacher/dashboard');
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TeacherShell>
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        <div className="rounded-3xl bg-white p-6 shadow-md sm:p-8">
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Teacher sign-in</h1>
          <p className="mt-2 text-base text-slate-600">
            Welcome back. Sign in to your authoring + live-session dashboard.
          </p>

          <button
            type="button"
            disabled
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-slate-200 bg-white px-6 py-4 text-base font-medium text-slate-400 shadow-sm"
            title="Coming soon"
          >
            Continue with Google for Education
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">Coming soon</span>
          </button>

          <div className="mt-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs uppercase tracking-wide text-slate-400">or</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3">
            <label className="text-sm text-slate-700" htmlFor="email">
              Email
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </label>
            <label className="text-sm text-slate-700" htmlFor="password">
              Password
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </label>
            {error && (
              <p className="text-sm text-rose-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="mt-2 rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 text-xs text-slate-500">
            Looking for the parent / kid app?{' '}
            <Link to="/" className="text-amber-700 underline-offset-2 hover:underline">
              Open Maysterko
            </Link>
            .
          </p>
        </div>
      </div>
    </TeacherShell>
  );
}
